/**
 * A linha de uma rolagem: <b>atributo + atributo + perícia + dado</b>.
 *
 * <p>Este template nasceu na tela de Combate e era copiado na Tier List. Agora é um só,
 * e por isso mora fora das duas: a Personagem mostra as dezoito, a Tier List mostra as
 * seis dela, e as duas desenham a mesma linha porque é a mesma conta.
 *
 * <p>A perícia no meio é <b>fixa</b> — é ela que dá nome à linha. O que se escolhe são os
 * dois atributos e o dado.
 */
import { PericiaIcone } from '../mundo/PericiaIcone';
import { ATRIBUTOS, atributoPorSigla } from './atributos';
import { DADOS, corDe, equacaoEmBranco } from './pericias';
import type { Equacao, Pericia } from './pericias';
import './rolagem.css';

export function LinhaDeRolagem({
  pericia,
  equacao,
  aoMudar,
  titulo,
}: {
  pericia: Pericia;
  equacao?: Equacao;
  aoMudar: (campo: keyof Equacao, valor: string) => void;
  /** O que a perícia é, numa frase — vira o `title` da linha. */
  titulo?: string;
}) {
  const cor = corDe(pericia);
  const depois = Boolean(pericia.depoisDaSeason1);
  const eq = equacao ?? equacaoEmBranco();

  return (
    <div
      className={`rl-linha${depois ? ' futura' : ''}`}
      style={{ '--c': cor } as React.CSSProperties}
      title={titulo}
    >
      <span className="rl-nome">
        <PericiaIcone pericia={pericia.id} cor={cor} tamanho={19} />
        {pericia.rotulo}
        {depois && <em>depois da Season 1</em>}
      </span>

      <span className="rl-conta">
        <SeletorDeAtributo
          valor={eq.atributoA}
          aoMudar={(v) => aoMudar('atributoA', v)}
          rotulo={`Primeiro atributo de ${pericia.rotulo}`}
          desativado={depois}
        />
        <span className="rl-op">+</span>
        <SeletorDeAtributo
          valor={eq.atributoB}
          aoMudar={(v) => aoMudar('atributoB', v)}
          rotulo={`Segundo atributo de ${pericia.rotulo}`}
          desativado={depois}
        />
        <span className="rl-op">+</span>
        <span className="rl-fixo">{pericia.rotulo}</span>
        <span className="rl-op">+</span>
        <select
          className="rl-campo rl-dado"
          value={eq.dado}
          onChange={(e) => aoMudar('dado', e.target.value)}
          disabled={depois}
          aria-label={`Dado de ${pericia.rotulo}`}
        >
          {DADOS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </span>
    </div>
  );
}

/** O seletor de atributo: mostra a sigla, e o nome inteiro no título. */
export function SeletorDeAtributo({
  valor,
  aoMudar,
  rotulo,
  desativado,
}: {
  valor: string;
  aoMudar: (v: string) => void;
  rotulo: string;
  desativado?: boolean;
}) {
  return (
    <select
      className="rl-campo rl-atributo"
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
      disabled={desativado}
      aria-label={rotulo}
      title={atributoPorSigla(valor)?.nome ?? rotulo}
    >
      <option value="">atributo</option>
      {ATRIBUTOS.map((a) => (
        <option key={a.sigla} value={a.sigla}>
          {a.sigla} · {a.nome}
        </option>
      ))}
    </select>
  );
}
